import { Button, InlineLoading } from '@carbon/react';
import { useTranslation } from '@pos/i18n';
import './common.scss';

/**
 * The unsaved-changes bar of the pages edited as a whole (prices, menu editor, order flow steps): a summary, what is
 * still wrong, and Discard / Save. Sticks to the bottom of the page until the changes are saved or discarded.
 *
 * Props: `summary` (e.g. "3 unsaved changes"), `problems` (translated messages shown under it, in the error colour),
 * `discardText`, `saveText`, `onDiscard()`, `onSave()`, `saveDisabled`, `saving`.
 */
export function SaveBar({
  summary,
  problems = [],
  discardText,
  saveText,
  onDiscard,
  onSave,
  saveDisabled,
  saving,
}: {
  summary: string;
  problems?: readonly string[];
  discardText: string;
  saveText: string;
  onDiscard(): void;
  onSave(): void;
  saveDisabled?: boolean;
  saving?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="pos-save-bar" role="region" aria-label={summary}>
      <div className="pos-save-bar__text">
        <strong>{summary}</strong>
        {problems.map((message) => (
          <span key={message} className="pos-save-bar__problem">
            {message}
          </span>
        ))}
      </div>
      <Button kind="secondary" onClick={onDiscard} disabled={saving}>
        {discardText}
      </Button>
      <Button kind="primary" disabled={saveDisabled || saving} onClick={onSave}>
        {saving ? <InlineLoading description={t('admin.saving')} /> : saveText}
      </Button>
    </div>
  );
}
